/**
 * Driving state for the sticky pill-nav (`TodayNav.svelte`) — which section is in
 * view, and the smooth jump a pill click performs.
 *
 * One shared `IntersectionObserver`, registered per-section through `spyOn` so
 * sections that mount and unmount with their data attach and detach cleanly.
 *
 * `rootMargin` and the section's own `scroll-margin-top` are one unit: the margin
 * shrinks the "active band" to the top of the viewport, just below the sticky bar,
 * so a pill flips as its heading reaches the top rather than when the section
 * barely peeks in from the bottom. Change the sticky bar's height and both move.
 */
export function createSectionSpy() {
  let active = $state('');
  let observer: IntersectionObserver | null = null;

  function ensure(): IntersectionObserver | null {
    // jsdom and the SSR pass have no IntersectionObserver. Returning null leaves
    // `active` empty, which renders every pill unhighlighted — not an error.
    if (observer || typeof IntersectionObserver === 'undefined') return observer;
    observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) active = (entry.target as HTMLElement).id;
      }
    }, { rootMargin: '-58px 0px -72% 0px', threshold: 0 });
    return observer;
  }

  return {
    get active() { return active; },
    /** `use:spy.spyOn` on a `<section id="…">`. The id is what the pill links to. */
    spyOn(node: HTMLElement) {
      const obs = ensure();
      obs?.observe(node);
      return { destroy() { obs?.unobserve(node); } };
    },
  };
}

/**
 * Smooth-scroll a pill click to its section, respecting that section's
 * `scroll-margin-top` so the sticky bar never covers the heading. The pill stays
 * an `<a href="#…">` for keyboard and no-JS fallback; this only replaces the
 * instant jump.
 */
export function jumpToSection(event: MouseEvent, id: string) {
  event.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
